import { css } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  OppositeTheme,
  Size,
  Spacing,
  Transition,
  ZIndex,
} from '@opallabs/gem'
import type { StyleProps } from './types'

export const container = ({ size }: StyleProps) =>
  css({
    // the check mark is always white, regardless of theme
    color: Color.dark.Text.Default,
    alignItems: 'center',
    blockSize: size,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    inlineSize: size,
    justifyContent: 'center',
    position: 'relative',
  })

export const checkbox = ({ size, theme }: StyleProps) =>
  css({
    appearance: 'none',
    backgroundColor: 'transparent',
    borderRadius: Size.Rounding,
    borderStyle: 'solid',
    borderWidth: Spacing.XS / 2,
    boxSizing: 'border-box',
    blockSize: size,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    inlineSize: size,
    inset: 0,
    margin: 0,
    position: 'absolute',
    zIndex: ZIndex.Element.Level1,
    ...Transition(['border-color', 'box-shadow', 'opacity']),
    borderColor: Color[theme].Surface.N300,

    '&::before': {
      borderRadius: Size.Rounding,
      content: '""',
      display: 'flex',
      // `-2` compensates for `borderWidth: 2`; without it `::before` is inset
      // by 2 pixels from the checkbox’s outer edge
      inset: Spacing.XS / -2,
      margin: 0,
      position: 'absolute',
      zIndex: 0,
      ...Transition(['background-color'], {
        curve: 'ease-out',
      }),
    },

    '&:checked': {
      backgroundColor: Color[theme].Surface.A200,
      borderColor: Color[theme].Surface.A200,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    // The ring is for the keyboard: a pointer already knows where it pressed.
    '&:focus-visible': {
      ...FocusIndicator[theme],
    },

    '&:focus:enabled, &:hover:enabled, &:active:enabled': {
      borderColor: Color[theme].Surface.A200,
    },
    '&:focus:enabled:checked, &:hover:enabled:checked, &:active:enabled:checked':
      {
        borderColor: Color[theme].Translucent.A00,
      },
    '&:focus:enabled, &:hover:enabled': {
      '&::before': {
        backgroundColor: Color[theme].Translucent.A12,
      },
      '&:checked::before': {
        backgroundColor: Color[OppositeTheme[theme]].Translucent.N12,
      },
    },
    '&:active:enabled': {
      '&::before': {
        backgroundColor: Color[theme].Translucent.A24,
      },
      '&:checked::before': {
        backgroundColor: Color[OppositeTheme[theme]].Translucent.N24,
      },
    },

    '@media (prefers-reduced-transparency: reduced)': {
      '&:not(:checked)': {
        backgroundColor: Color[theme].Surface.N000,
      },
    },

    '@media (prefers-contrast: more)': {
      '&:checked, &:not(:checked)': {
        backgroundColor: Color[OppositeTheme[theme]].Surface.N000,
      },
    },
  })

export const icon = css({
  blockSize: 'unset',
  display: 'flex',
  inset: Spacing.XS / 2,
  pointerEvents: 'none',
  zIndex: ZIndex.Element.Level1,
})
